import React, { Component } from 'react'
import { inject, observer, PropTypes } from 'mobx-react'
import { toJS } from 'mobx'
import DocumentTitle from 'react-document-title'

import { expertApi } from '@atheneum/api'
import { formatDate } from '@atheneum/api/dist/lib'
import ACL from '@components/ACL'
import Actions from './Actions'
import Experience from './Experience'
import PersonalInfo from './PersonalInfo'
import Attachment, { confirmDelete } from '@components/Attachment'
import Comments from '@components/Comments'
import Paragraph from '@components/Details/Paragraph'
import get from 'lodash/get'

class Details extends Component {
  static propTypes = {
    expertStore: PropTypes.objectOrObservableObject,
    commentStore: PropTypes.objectOrObservableObject
  }

  buildErrorGrowlProps = type => {
    return {
      title: 'Error!',
      message: `${type} is required.`,
      type: 'error',
      autoClose: true
    }
  }

  createExpert = async () => {
    const { expertStore } = this.props
    const { expert, growlStore } = expertStore

    await expertStore.get(expert.id)
    const expertProps = toJS(this.props.expertStore.expert)

    if (!expertProps.email && !expertProps.address.emails.length) {
      growlStore.addNotification(this.buildErrorGrowlProps('Email'))
      return false
    } else if (!expertProps.experiences.length) {
      growlStore.addNotification(this.buildErrorGrowlProps('Experience'))
      return false
    } else if (!expertProps.languages.length) {
      growlStore.addNotification(this.buildErrorGrowlProps('Language'))
      return false
    } else if (!expertProps.title) {
      growlStore.addNotification(this.buildErrorGrowlProps('Title'))
      return false
    } else {
      const createdExpert = await expertApi.create(expertProps)
      return createdExpert
    }
  }

  handleResetPassword = async () => {
    const { expertStore } = this.props
    const { expert, growlStore } = expertStore

    let forgotExpert = expert

    if (!Number(expert.id)) {
      forgotExpert = await this.createExpert()
    }

    return expertApi.forgotPassword(forgotExpert.email)
      .then(res => {
        growlStore.addNotification({
          title: 'Success!',
          message: 'Reset password link sent.',
          type: 'success',
          autoClose: true
        })
        if (!Number(expert.id)) {
          this.props.history.replace(
            `${this.props.history.location.pathname.includes('expert-search')
              ? '/expert-search'
              : ''
            }/expert/${forgotExpert.id}`
          )
        }
      })
      .catch(err => {
        growlStore.addNotification({
          title: 'Error!',
          message: err.message || 'There was an error.',
          type: 'error',
          autoClose: true
        })
      })
  }

  render() {
    if (this.props.expertStore.loading) return null
    const { expert, expertCopy } = this.props.expertStore

    if (!expertCopy) {
      return null
    }

    const { commentStore } = this.props
    const lastLogin = expertCopy.lastActive
      ? formatDate(expertCopy.lastActive)
      : 'N/A'
    const dateCreated = formatDate(expertCopy.created_at)
    const experiences = get(expertCopy, 'cv.experiences', [])

    return (
      <DocumentTitle
        title={`${expertCopy.firstName} ${expertCopy.lastName} - profile | Atheneum partners`}>
        <div className="details-wrapper animate-intro">
          <Actions commentStore={commentStore} expert={expert} />
          <div className="content">
            <div className="col-2">
              <Comments
                collapsable
                commentStore={commentStore}
                history={this.props.history}
                modelId={expert.id}
                model="expert"
              />
              <Paragraph
                title="Industry experience"
                value={expertCopy.expert?.industryExpertise}
                tooltip={
                  <p>
                    This is information used to fill in the public profiles on
                    projects and to be presented to client.
                  </p>
                }
              />
              <Paragraph
                title="Expert experience"
                value={expertCopy.expert?.expertIndustryExperience}
                tooltip={
                  <p>
                    Cannot edit expert experience. This information is present
                    on expert interface and is fully manageable by experts. If
                    you require changes, please contact ERM team.
                  </p>
                }
              />
              <Attachment
                attachments={this.props.expertStore.attachments}
                url={
                  '/api/expert/' +
                  this.props.expertStore.expert.id +
                  '/attachments'
                }
                preUpload={async () => {
                  if (!Number(expert.id)) {
                    const expert = await this.createExpert()

                    return `/api/expert/${expert.id}/attachments`
                  }
                }}
                onDelete={fileId =>
                  confirmDelete(fileId, this.props.expertStore.deleteAttachment)
                }
                onOk={res => {
                  if (!Number(expert.id)) {
                    setTimeout(() => {
                      this.props.history.replace(
                        `${this.props.history.location.pathname.includes(
                          'expert-search'
                        )
                          ? '/expert-search'
                          : ''
                        }/expert/${res.data[0].expertId}`
                      )
                    }, 200)
                  }
                  this.props.expertStore.getAttachments(
                    this.props.expertStore.expert.id
                  )
                }}
                tooltip={
                  <p>
                    Please keep file names unique and descriptive so that users
                    can recognize what the file is. Do not upload duplicate
                    files. Would you want to replace it, delete the old one
                    first.
                  </p>
                }
              />
            </div>
            <div className="col-2 col-2--padding">
              <PersonalInfo expert={expertCopy} />
            </div>
          </div>
          <div className="experience-wrapper">
            <Experience experiences={experiences} cv={expertCopy.cv} />
          </div>
          <div className="comments-wrapper">
            <p className="independent-label">
              <span>Last login:</span> {lastLogin}
            </p>
            <p className="independent-label">
              <span>Date created:</span> {dateCreated}
            </p>
            <p className="independent-label">
              <span>Newsletter: </span>
              {expert.expert?.newsletter ? 'Subscribed' : '-'}
            </p>
            <p className="independent-label">
              <span>Interview reminders via SMS: </span>
              {expert.expert?.sendSmsReminders ? 'Yes' : 'No'}
            </p>
            <ACL roles={['AdvancedExpertManagement']}>
              <p
                className="independent-label independent-label--blue"
                onClick={this.handleResetPassword}>
                Reset password
              </p>
            </ACL>
          </div>
        </div>
      </DocumentTitle>
    )
  }
}

export default inject(
  'expertStore',
  'modalStore',
  'commentStore'
)(observer(Details))
